import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, FileText, Maximize2, Minimize2, Pencil } from 'lucide-react';
import { TeacherProfile } from '@/hooks/useTeacherProfiles';
import { TeacherInfo } from './TeacherInfo';
import { StatusBadge } from './StatusBadge';
import { GradeLevelSubjects } from './GradeLevelSubjects';
import { SpecializedBadges } from './SpecializedBadges';
import { AvailabilityDisplay } from './AvailabilityDisplay';
import { transformSubjects } from './utils/subjectsTransformer';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TeacherProfilesTableProps {
  profiles: TeacherProfile[];
  isLoading: boolean;
}

export const TeacherProfilesTable: React.FC<TeacherProfilesTableProps> = ({
  profiles,
  isLoading,
}) => {
  const navigate = useNavigate();
  const [modalContent, setModalContent] = useState<{ title: string; content: string } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFullView, setIsFullView] = useState(false);

  const handleViewDetail = (teacherCode: string) => {
    navigate(`/admin/teachers/${teacherCode}`);
  };

  const handleViewText = (title: string, content: string) => {
    setModalContent({ title, content });
    setIsModalOpen(true);
  };

  const toggleFullView = () => {
    setIsFullView(!isFullView);
  };

  const renderSkeletonRows = () => {
    if (isFullView) {
      return Array(10)
        .fill(0)
        .map((_, index) => (
          <TableRow key={`skeleton-${index}`}>
            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
            <TableCell><Skeleton className="h-4 w-96" /></TableCell>
            <TableCell><Skeleton className="h-4 w-96" /></TableCell>
          </TableRow>
        ));
    }
    
    return Array(10)
      .fill(0)
      .map((_, index) => (
        <TableRow key={`skeleton-${index}`}>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
        </TableRow>
      ));
  };

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold">รายชื่อครู</h2>
        <Button
          variant="outline"
          onClick={toggleFullView}
          className="flex items-center gap-2"
        >
          {isFullView ? (
            <>
              <Minimize2 className="h-4 w-4" />
              แสดงแบบย่อ
            </>
          ) : (
            <>
              <Maximize2 className="h-4 w-4" />
              แสดงประสบการณ์และผลงานวิชาการแบบเต็มหน้า
            </>
          )}
        </Button>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ข้อมูลครู</TableHead>
              {isFullView ? (
                <>
                  <TableHead>ประสบการณ์</TableHead>
                  <TableHead>ผลงานวิชาการ</TableHead>
                </>
              ) : (
                <>
                  <TableHead>ประสบการณ์</TableHead>
                  <TableHead>ผลงานวิชาการ</TableHead>
                  <TableHead>เฉพาะทาง</TableHead>
                  <TableHead>ประถมต้น</TableHead>
                  <TableHead>ประถมปลาย</TableHead>
                  <TableHead>ม.ต้น</TableHead>
                  <TableHead>ม.ปลาย</TableHead>
                  <TableHead className="w-[130px]">ตารางเวลา</TableHead>
                  <TableHead>จัดการ</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              renderSkeletonRows()
            ) : profiles.length > 0 ? (
              profiles.map((profile) => {
                const transformedSubjects = transformSubjects(profile.subjects);
                
                // Create map for each grade_level
                const subjectsByGrade = new Map<string, Array<{ subject: string; course_types: string[] }>>();
                transformedSubjects.forEach(gradeData => {
                  subjectsByGrade.set(gradeData.grade_level, gradeData.subjects);
                });

                return (
                  <TableRow key={profile.teacher_code}>
                    <TableCell className="font-medium">
                      <div className="space-y-2">
                        <div className="space-y-1">
                          <div className="text-sm font-semibold text-blue-600">
                            {profile.teacher_code}
                          </div>
                          <div className="font-medium">
                            {profile.full_name}
                          </div>
                          {profile.nickname && (
                            <div className="text-sm text-muted-foreground">
                              ({profile.nickname})
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={profile.status} />
                          <span className="text-sm text-muted-foreground">
                            {profile.phone_number || '-'}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    {isFullView ? (
                      <>
                        <TableCell className="max-w-md">
                          <div className="whitespace-pre-wrap text-sm">
                            {profile.teaching_experience || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="whitespace-pre-wrap text-sm">
                            {profile.academic_achievements || '-'}
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>
                          {profile.teaching_experience && profile.teaching_experience !== '-' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewText('ประสบการณ์การสอน', profile.teaching_experience)}
                              className="flex items-center gap-1"
                            >
                              <FileText className="h-4 w-4" />
                              ดู
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {profile.academic_achievements && profile.academic_achievements !== '-' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewText('ผลงานวิชาการ', profile.academic_achievements)}
                              className="flex items-center gap-1"
                            >
                              <FileText className="h-4 w-4" />
                              ดู
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <SpecializedBadges specialized={profile.specialized || []} />
                        </TableCell>
                        <GradeLevelSubjects subjects={subjectsByGrade.get('ประถมต้น') || []} />
                        <GradeLevelSubjects subjects={subjectsByGrade.get('ประถมปลาย') || []} />
                        <GradeLevelSubjects subjects={subjectsByGrade.get('ม.ต้น') || []} />
                        <GradeLevelSubjects subjects={subjectsByGrade.get('ม.ปลาย') || []} />
                        <TableCell className="w-[130px]">
                          <AvailabilityDisplay templates={profile.availability_templates || []} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewDetail(profile.teacher_code)}
                              className="flex items-center gap-1"
                            >
                              <Eye className="h-4 w-4" />
                              ดู
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/teachers/${profile.teacher_code}/edit`)}
                              className="flex items-center gap-1"
                            >
                              <Pencil className="h-4 w-4" />
                              แก้ไข
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={isFullView ? 3 : 10} className="text-center py-4">
                  ไม่พบข้อมูลครู
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{modalContent?.title}</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap">{modalContent?.content}</div>
        </DialogContent>
      </Dialog>
    </>
  );
};
